import { Context } from 'vuex-smart-module'
import VueRouter, { Route, NavigationGuardNext } from 'vue-router'
import { UserProfile } from '@/typings/api/User'
import { InitPluginsOptions } from '@/typings/common'
import { authStoreModule } from '@/views/Auth/store'
import {
  allPermission,
  authFormDataPermissions,
  authUserPermissions,
  lkParticipantPermissions,
  lkPublicationPermissions,
  lkPublicationStorageFilePermissions,
} from '@/helpers/enums/permission-models'
import { ERRORS_ROUTES } from '@/views/Errors/utils'
import { ERROR_403 } from '@/helpers/getErrorResponse'

type AuthContext = Context<typeof authStoreModule>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * @param profile Профиль пользователя
 * @param permission Проверяемое разрешение
 * @param allowSu true, если проверять наличие свойства is_superuser. Для некоторых случаев, это свойство не должно играть роли в разрешении доступа ко внешним сервисам.
 * @returns Boolean
 */
export const accessBase = (
  profile: UserProfile | null,
  permission?: string,
  allowSu = true,
): boolean => {
  if (allowSu && profile?.is_superuser) {
    return true
  }
  return permission === allPermission
  || (!!permission && !!profile?.permissions && permission in profile.permissions)
}

export const accessAdmin = (
  profile: UserProfile | null,
  permission?: string,
): boolean => profile?.is_admin === true
  && !!permission
  && [
    lkParticipantPermissions.read,
    lkPublicationPermissions.read,
    authFormDataPermissions.read,
    authUserPermissions.read,
    lkPublicationStorageFilePermissions.read,
  ].includes(permission)

const createRouterGuardPermission = (router: VueRouter) => {
  const routerGuardPermission = (
    to: Route,
    from: Route,
    next: NavigationGuardNext,
  ) => {
    // родительские permission не рассматриваются
    const { permission, permissions } = to?.meta as {
      permission?: string;
      permissions?: string[];
    }

    if (!(permission || permissions)) {
      next()
      return
    }

    // TODO: как-то определить стратегию - all или oneOf
    const hasAccess = permission
      ? router.app.$access(permission)
      : permissions?.some(router.app.$access)

    if (hasAccess) {
      next()
    } else {
      const params = {
        fromRoute: to.fullPath,
        code: (ERROR_403 as unknown) as string,
      }
      next({ name: ERRORS_ROUTES.ERRORS, params })
    }
  }

  return router.beforeEach(routerGuardPermission)
}

const add$accessToProto = (proto: unknown, storeModule: AuthContext): void => void Object.defineProperty(proto, '$access', {
  get(this: Vue) {
    // eslint-disable-next-line no-underscore-dangle
    if (this._access) return this._access

    const { access } = (this.$options as unknown) as {
        access?: typeof accessBase;
      }

    if (access) {
      const $access = (
        permission: string, allowSu = true,
      ) => access.call(this, storeModule.getters.profile, permission, allowSu)
      // eslint-disable-next-line no-underscore-dangle
      this._access = $access
      return $access
    }

    // eslint-disable-next-line
      return this.$parent.$access;
  },
})

export const initPlugin = (options: InitPluginsOptions) => {
  // eslint-disable-next-line object-curly-newline
  const {
    Vue: { prototype },
    app,
    store,
    router,
  } = options
  const auth = authStoreModule.context(store)
  // eslint-disable-next-line no-param-reassign
  app.access = (
    profile: UserProfile | null,
    permission?: string,
    allowSu = true,
  ) => accessBase(profile, permission, allowSu)
    || accessAdmin(profile, permission)

  createRouterGuardPermission(router)
  add$accessToProto(prototype, auth)
}
